import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { changeUserPassword } from '../core/_requests';
import { useMutation } from '@tanstack/react-query';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  initialUserChangePasswordRequestDto,
  type UserChangePasswordRequestDto,
} from '../../../../models/UserChangePasswordRequestDto';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { useState } from 'react';
import PasswordChecklist from 'react-password-checklist';

const formSchema = Yup.object().shape({
  password: Yup.string().required('Senha atual é obrigatória'),
  new_password: Yup.string().required('Nova senha é obrigatória'),
});

type Props = {
  show: boolean;
  onClose: () => void;
};

function EditPasswordModal({ show, onClose }: Props) {
  const navigate = useNavigate();

  const [newPasswordCheck, setNewPasswordCheck] = useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const formik = useFormik<UserChangePasswordRequestDto>({
    initialValues: initialUserChangePasswordRequestDto,
    validationSchema: formSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (values.new_password !== newPasswordCheck) {
        SystemNotification.error('A nova senha e a confirmação não conferem');
        return;
      }
      editMutation.mutate(values);
    },
  });

  const editMutation = useMutation({
    mutationFn: (newPassword: UserChangePasswordRequestDto) => changeUserPassword(newPassword),

    onSuccess: () => {
      SystemNotification.success('Senha alterada com sucesso');
      onClose();
      navigate('', { state: { refreshDatatable: true } });
    },

    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  const handleExited = () => {
    formik.resetForm();
    setNewPasswordCheck('');
  };

  return (
    <Modal show={show} onExited={handleExited} onHide={onClose}>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Editar senha de acesso</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body d-flex flex-column flex-sm-row items-center justify-content-around">
          <div className="justify-content-center w-100 px-1 px-md-8">
            <div className="d-flex flex-column gap-7">
              <div className="form-group">
                <label className="form-label fs-6 fw-bold text-gray-700 required">Senha atual</label>
                <input
                  className="form-control"
                  id="password"
                  placeholder="Senha atual"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                />
                {!!formik.errors.password && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.password}</div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label fs-6 fw-bold text-gray-700 required">Nova senha</label>
                <input
                  className="form-control"
                  id="new_password"
                  placeholder="Nova senha"
                  type="password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                />
                {!!formik.errors.new_password && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.new_password}</div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label fs-6 fw-bold text-gray-700 required">Confirmação de nova senha</label>
                <input
                  className="form-control"
                  id="new_password_check"
                  placeholder="Repita a nova senha"
                  type="password"
                  value={newPasswordCheck}
                  onChange={(value) => setNewPasswordCheck(value.target.value)}
                />
                {!!formik.errors.new_password && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.new_password}</div>
                  </div>
                )}
              </div>
              <div className="d-flex flex-column justify-content-center align-items-center my-2">
                <span className="fw-bold text-gray-600 mb-3 fs-6">Sua senha deve conter:</span>
                <PasswordChecklist
                  iconSize={12}
                  invalidColor="#D12446"
                  messages={{
                    minLength: 'Mínimo 6 caracteres',
                    letter: 'Pelo menos uma letra',
                    number: 'Pelo menos um número',
                    match: 'As senhas não coincidem',
                  }}
                  minLength={5}
                  rules={['minLength', 'letter', 'number', 'match']}
                  validColor="#4BCA81"
                  value={formik.values.new_password}
                  valueAgain={newPasswordCheck}
                  onChange={(isValid) => setIsPasswordValid(isValid)}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="text-gray-600" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="btn btn-primary" disabled={editMutation.isPending || !isPasswordValid} type="submit">
            {editMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default EditPasswordModal;
